import React from 'react';
import { View, Text } from 'react-native';
import { createAction, createReducer } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import i18n from 'i18n-js';

import { backendCall, createApiCall } from '@reducers/utils';
import { Alert, scale, setSecureValue, getSecureValue, setTZ } from '@helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { RootState, AppThunk } from '@store/types';
import type { TimeState, SetServerTimePayload, SetPublicTimePayload } from './types';

// Re-export types for consumers
export type { TimeState, ServerTimeInfo, PublicServerTime } from './types';

// === Initial State ===

const initState: TimeState = {
  server: {},
  publicServer: {
    offset: 0,
  },
  syncTime: null,
  loaded: false,
};

// === Selectors ===

const selectTimeState = (state: RootState) => state.time as TimeState;

export const selectServerOffset = (state: RootState) => selectTimeState(state).server?.offset;
export const selectTimeLoaded = (state: RootState) => selectTimeState(state).loaded;
export const selectServerTime = (state: RootState) => selectTimeState(state).server;
export const selectSyncTime = (state: RootState) => selectTimeState(state).syncTime;
export const selectFactoryTimeZone = (state: RootState) => selectTimeState(state).server?.factoryTimeZoneIana;

// === Actions ===

export const setPublicTime = createAction<SetPublicTimePayload>('time/setPublicTime');
export const setServerTime = createAction<SetServerTimePayload>('time/setServerTime');
export const setSyncTime = createAction('time/setSyncTime');
export const clear = createAction('clear');

// === Module-level state ===

let timeOffsetAlertWasShow = false;

// === Thunks ===

export const getTimeSetup = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const time = getState().time as TimeState;

  if (time?.server?.factoryTimeZoneIana) {
    setTZ(time.server.factoryTimeZoneIana);
  }

  const startServer = moment.utc();

  // Using createApiCall for type-safe backendCall - response type is inferred from api.time.checkTimeInfo
  const serverTime = await dispatch(
    backendCall(createApiCall((api) => api.time.checkTimeInfo, { ignoreDefaultErrorHandler: true }))
  );

  const endServer = moment.utc();

  if (!serverTime) {
    dispatch(
      setServerTime({
        offset: 0,
      })
    );
    return;
  }

  const userTimeOffset = -new Date().getTimezoneOffset() / 60;
  const serverTimeOffset = parseInt(serverTime.factoryTimeZoneOffset || '0');
  setTZ(serverTime.factoryTimeZoneIana || '');

  const storedUserTimeOffset = getSecureValue('userTimeOffset');
  const storedUserTimeOffsetNum = storedUserTimeOffset !== null ? Number(storedUserTimeOffset) : null;

  if (userTimeOffset != serverTimeOffset && !timeOffsetAlertWasShow && storedUserTimeOffsetNum !== userTimeOffset) {
    Alert({
      title: i18n.t('Your device time is different from your factory time'),
      text: ({ C, font }: { C: (color: string) => string; font: Record<string, object> }) => (
        <View>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(8) }]}>
            {i18n.t('Device time')}: {moment().format('HH:mm')}
          </Text>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
            {i18n.t('Factory time')}: {moment().utcOffset(serverTimeOffset).format('HH:mm')}
          </Text>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(18) }]}>
            {i18n.t('The times that you see in this App are based on the factory time')}
          </Text>
        </View>
      ),
      actions: [
        {
          style: 'primary',
          text: 'OK',
          testID: TEST_IDS.factoryTimeAlert.okButton,
          onPress: () => {
            timeOffsetAlertWasShow = true;
          },
        },
      ],
    });
  }

  setSecureValue('userTimeOffset', JSON.stringify(userTimeOffset));

  if (serverTime && serverTime.platformDateTime) {
    const serverRequestDuration = Number(endServer.format('x')) - Number(startServer.format('x'));
    const serverUTC = moment.utc(serverTime.platformDateTime).add(serverRequestDuration / 2, 'milliseconds');
    const serverOffset = Number(serverUTC.format('x')) - Number(moment.utc().format('x'));
    dispatch(
      setServerTime({
        ...serverTime,
        offset: serverOffset,
      })
    );
  }

  dispatch(setSyncTime());
};

// === Reducer ===

export default createReducer<TimeState>(initState, (builder) => {
  builder
    .addCase(setServerTime, (state, action) => {
      state.server = action.payload;
    })
    .addCase(setPublicTime, (state, action) => {
      state.publicServer = action.payload;
    })
    .addCase(setSyncTime, (state) => {
      state.syncTime = moment().format('HH:mm:ss');
      state.loaded = true;
    })
    .addCase(clear, () => initState);
});
